import React, { useState, useEffect } from 'react';
import { donationAPI } from '../../services/api';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Modal, Button } from 'react-bootstrap';

const Donations = () => {
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState([]);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedDonation, setSelectedDonation] = useState(null);

    // ── Fetch donations ──
    const fetchDonations = async () => {
        setLoading(true);
        try {
            const res = await donationAPI.getAll();
            setItems(res.data.data);
        } catch (error) {
            console.error('Error fetching donations:', error);
            toast.error('Failed to load donations');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDonations();
    }, []);

    // ── View details ──
    const handleViewDetails = (donation) => {
        setSelectedDonation(donation);
        setShowDetailModal(true);
    };

    // ── Delete with confirmation ──
    const confirmDelete = (id) => {
        toast.info(
            <div>
                <p style={{ marginBottom: '0.5rem' }}>Are you sure you want to delete this donation record?</p>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                    <button
                        className="btn btn-sm btn-danger"
                        style={{ padding: '0.25rem 0.75rem', fontSize: '0.875rem', background: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                        onClick={() => { toast.dismiss(); handleDeleteItem(id); }}
                    >
                        Yes
                    </button>
                    <button
                        className="btn btn-sm btn-secondary"
                        style={{ padding: '0.25rem 0.75rem', fontSize: '0.875rem', background: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                        onClick={() => toast.dismiss()}
                    >
                        No
                    </button>
                </div>
            </div>,
            { autoClose: false, closeOnClick: false, closeButton: false, icon: false }
        );
    };

    const handleDeleteItem = async (id) => {
        setLoading(true);
        try {
            await donationAPI.delete(id);
            toast.success('Donation deleted successfully');
            fetchDonations();
        } catch (error) {
            console.error(error);
            toast.error('Failed to delete donation');
        } finally {
            setLoading(false);
        }
    };

    // ── Format date ──
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    // ── Inline styles (consistent with Photos/Videos) ──
    const mainContentStyle = {
        marginLeft: '260px',
        padding: '20px 30px',
        backgroundColor: '#f4f7fc',
        minHeight: '100vh',
    };

    const contentWrapperStyle = {
        maxWidth: '1200px',
        margin: '0 auto',
    };

    const pageHeaderStyle = {
        background: 'white',
        padding: '20px 30px',
        borderBottom: '1px solid #e2e8f0',
        marginBottom: '1.5rem',
    };

    const pageTitleStyle = {
        fontSize: '1.3rem',
        fontWeight: 700,
        color: '#0b2b3b',
        margin: 0,
    };

    const pageSubtitleStyle = {
        fontSize: '0.8rem',
        color: '#64748b',
        marginTop: '5px',
    };

    const listCardStyle = {
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        overflow: 'hidden',
    };

    const tableContainerStyle = {
        overflowX: 'auto',
        padding: '0 1rem',
    };

    const tableStyle = {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '0.85rem',
    };

    const tableHeaderStyle = {
        padding: '14px 20px',
        fontSize: '0.75rem',
        fontWeight: 600,
        color: '#64748b',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        borderBottom: '1px solid #e2e8f0',
        background: '#f8fafc',
        textAlign: 'left',
    };

    const tableCellStyle = {
        padding: '14px 20px',
        borderBottom: '1px solid #f1f5f9',
        verticalAlign: 'middle',
    };

    const badgeStyle = (status) => {
        const colors = {
            pending: { bg: '#fef3c7', text: '#92400e' },
            paid: { bg: '#e9f7ef', text: '#1e7b48' },
            failed: { bg: '#fce4e4', text: '#a11f1f' },
            refunded: { bg: '#e2e8f0', text: '#475569' },
        };
        const color = colors[status] || colors.pending;
        return {
            background: color.bg,
            color: color.text,
            fontWeight: 600,
            padding: '4px 12px',
            borderRadius: '40px',
            fontSize: '0.7rem',
            display: 'inline-block',
            textTransform: 'capitalize',
        };
    };

    const actionButtonStyle = {
        padding: '0.25rem 0.5rem',
        fontSize: '0.75rem',
        borderRadius: '4px',
        border: 'none',
        cursor: 'pointer',
        marginRight: '0.25rem',
    };

    return (
        <>
            <div style={mainContentStyle}>
                <div style={contentWrapperStyle}>
                    <div style={pageHeaderStyle}>
                        <h1 style={pageTitleStyle}>
                            <i className="fas fa-hand-holding-heart" style={{ marginRight: '0.5rem' }}></i>
                            Donations
                        </h1>
                        <p style={pageSubtitleStyle}>
                            View and manage all donation records
                        </p>
                    </div>

                    <div style={listCardStyle}>
                        <div style={tableContainerStyle}>
                            <table style={tableStyle}>
                                <thead>
                                    <tr>
                                        <th style={tableHeaderStyle}>ID</th>
                                        <th style={tableHeaderStyle}>Donor</th>
                                        <th style={tableHeaderStyle}>Phone</th>
                                        <th style={tableHeaderStyle}>Email</th>
                                        <th style={tableHeaderStyle}>Amount</th>
                                        <th style={tableHeaderStyle}>Category</th>
                                        <th style={tableHeaderStyle}>Status</th>
                                        <th style={tableHeaderStyle}>Date</th>
                                        <th style={tableHeaderStyle}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((item) => (
                                        <tr
                                            key={item.id}
                                            style={{ transition: 'background 0.15s' }}
                                            onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                        >
                                            <td style={tableCellStyle}>{item.id}</td>
                                            <td style={tableCellStyle}>{item.donor_name || 'N/A'}</td>
                                            <td style={tableCellStyle}>{item.donor_phone || 'N/A'}</td>
                                            <td style={tableCellStyle}>{item.donor_email || 'N/A'}</td>
                                            <td style={tableCellStyle}>₹{item.amount}</td>
                                            <td style={tableCellStyle}>{item.donor_category || '—'}</td>
                                            <td style={tableCellStyle}>
                                                <span style={badgeStyle(item.status)}>{item.status}</span>
                                            </td>
                                            <td style={tableCellStyle}>{formatDate(item.created_at)}</td>
                                            <td style={tableCellStyle}>
                                                <button
                                                    style={{ ...actionButtonStyle, background: '#17a2b8', color: 'white' }}
                                                    onClick={() => handleViewDetails(item)}
                                                >
                                                    <i className="fas fa-eye"></i> View
                                                </button>
                                                {/* <button
                                                    style={{ ...actionButtonStyle, background: '#dc3545', color: 'white' }}
                                                    onClick={() => confirmDelete(item.id)}
                                                >
                                                    <i className="fas fa-trash"></i>
                                                </button> */}
                                            </td>
                                        </tr>
                                    ))}
                                    {items.length === 0 && !loading && (
                                        <tr>
                                            <td colSpan="8" style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                                                <i className="fas fa-inbox" style={{ fontSize: '2rem', display: 'block', marginBottom: '0.5rem' }}></i>
                                                No donations yet.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <ToastContainer position="top-right" autoClose={3000} />
                    </div>
                </div>
            </div>

            {/* ── Detail Modal ── */}
            {/* ── Custom Modal (fallback) ── */}
            {showDetailModal && selectedDonation && (
                <div style={{
                    position: 'fixed',
                    top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999
                }} onClick={() => setShowDetailModal(false)}>
                    <div style={{
                        backgroundColor: 'white',
                        maxWidth: '700px',
                        width: '90%',
                        maxHeight: '80vh',
                        overflow: 'auto',
                        padding: '24px',
                        borderRadius: '8px',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
                    }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <h4 style={{ margin: 0 }}>Donation Details</h4>
                            <button
                                onClick={() => setShowDetailModal(false)}
                                style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}
                            >
                                ×
                            </button>
                        </div>
                        {/* Your content – same as in Modal.Body */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem' }}>
                            <div style={{ flex: '1 0 45%' }}><strong>Donor:</strong> {selectedDonation.donor_name || 'N/A'}</div>
                            <div style={{ flex: '1 0 45%' }}><strong>Email:</strong> {selectedDonation.donor_email || 'N/A'}</div>
                            <div style={{ flex: '1 0 45%' }}><strong>Phone:</strong> {selectedDonation.donor_phone || 'N/A'}</div>
                            <div style={{ flex: '1 0 45%' }}><strong>Amount:</strong> ₹{selectedDonation.amount}</div>
                            <div style={{ flex: '1 0 45%' }}><strong>Category:</strong> {selectedDonation.donor_category || '—'}</div>
                            <div style={{ flex: '1 0 45%' }}>
                                <strong>Status:</strong>{' '}
                                <span style={badgeStyle(selectedDonation.status)}>{selectedDonation.status}</span>
                            </div>
                            {selectedDonation.donor_address && (
                                <div style={{ flex: '1 0 100%' }}><strong>Address:</strong> {selectedDonation.donor_address}</div>
                            )}
                            {selectedDonation.donor_state && (
                                <div style={{ flex: '1 0 45%' }}><strong>State:</strong> {selectedDonation.donor_state}</div>
                            )}
                            {selectedDonation.donor_country && (
                                <div style={{ flex: '1 0 45%' }}><strong>Country:</strong> {selectedDonation.donor_country}</div>
                            )}
                            <div style={{ flex: '1 0 100%' }}><strong>Order ID:</strong> {selectedDonation.order_id}</div>
                            {selectedDonation.payment_id && (
                                <div style={{ flex: '1 0 100%' }}><strong>Payment ID:</strong> {selectedDonation.payment_id}</div>
                            )}
                            <div style={{ flex: '1 0 100%' }}><strong>Receipt Number:</strong> {selectedDonation.receipt_number}</div>
                            <div style={{ flex: '1 0 100%' }}><strong>Date:</strong> {formatDate(selectedDonation.created_at)}</div>
                        </div>
                        {selectedDonation.meta && selectedDonation.meta.notes && (
                            <>
                                <hr />
                                <div>
                                    <strong>Notes:</strong>
                                    <p style={{ whiteSpace: 'pre-wrap', background: '#f8fafc', padding: '1rem', borderRadius: '8px', marginTop: '0.5rem' }}>
                                        {selectedDonation.meta.notes}
                                    </p>
                                </div>
                            </>
                        )}
                        <div style={{ textAlign: 'right', marginTop: '1rem' }}>
                            <button
                                onClick={() => setShowDetailModal(false)}
                                style={{ padding: '0.5rem 1.5rem', background: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Donations;